import { PropsWithChildren, useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Stack,
  Link,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Typography,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import { Skill } from "@backend-types";
import { IconButtonContained } from "./utils/IconButtonContained";
import { IconButtonOutlined } from "./utils/IconButtonOutlined";
import { makeStyles } from "@mui/styles";
import { ReactComponent as IconBookmark } from "../../assets/IconBookmark.svg";
import { ReactComponent as IconBookmarkFill } from "../../assets/IconBookmarkFill.svg";
import { ReactComponent as IconChevronLeft } from "../../assets/IconChevronLeft.svg";
import { ReactComponent as IconChevronRight } from "../../assets/IconChevronRight.svg";
import { ReactComponent as VideoBlank } from "../../assets/video-blank.svg";

import { useHistory } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { UserCtx, DbUser } from "../../interfaces";

import postRobot from "post-robot";
import { generateGtmEvent } from "../../Helpers";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiPaper-root": {
      overflow: "visible",
      borderRadius: 15,
      border: `2px solid ${theme.palette.divider}`,
      margin: 0,
    },
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  videoContent: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },
  loaderBox: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  iframeStyle: {
    flexGrow: 1,
  },
  dialogContent: {},
  skillTitle: {
    fontSize: "1.31em",
  },
  bookmarkButton: {
    marginLeft: "auto",
    width: 40,
    height: 40,
  },
  slideButton: {
    marginRight: 18,
    width: 18,
    height: 18,
  },
  slideButtonActive: {},
  slideButtonInactive: {
    borderColor: theme.palette.primary.main,
    borderWidth: 1,
  },
  controlsButtonSize: {
    width: 54,
    height: 54,
  },
  controlsButton: {
    position: "absolute",
  },
  previousButton: {
    left: -74,
    top: "calc(50% - 54px)",
  },
  nextButton: {
    right: -74,
    top: "calc(50% - 54px)",
  },
}));

type SkillVideoModalProps = PropsWithChildren<{
  open: boolean;
  skills: Skill[] | undefined | any[];
  showControls?: boolean;
  onClose: () => void;
  showBottomNavigation?: boolean;
  playVideoIndex?: number;
  gtmSection?: string;
  embedMode?: boolean;
  onClickViewAllVideos?: () => void;
}>;

export function SkillVideoModal(props: SkillVideoModalProps) {
  const {
    showControls = false,
    showBottomNavigation = true,
    playVideoIndex = 0,
    embedMode = false,
    onClickViewAllVideos,
  } = props;
  const [currentSlide, setCurrentSlide] = useState(0);
  const classes = useStyles();
  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const currentUser: DbUser = userCtx.dbUser;
  const iFrameRef = useRef<HTMLIFrameElement | HTMLIFrameElement>(null);

  const currentVideo = props.skills && props.skills[currentSlide];
  const iframeSrc = currentVideo?.content ?? "";
  const videoTitle = (currentVideo && currentVideo?.title) ?? "";
  const [currentVideoSaved, setCurrentVideoSaved] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (props.open) {
      setLoaded(false);
    }
  }, [props.open, currentSlide]);

  useEffect(() => {
    if (props.open) {
      setCurrentSlide(playVideoIndex);
    }
  }, [playVideoIndex, props.open]);

  const handleChangeSlide = (slide: number) => {
    generateGtmEvent({
      event: "select",
      action: "click",
      name: "onclick",
      type: "pagination",
      region: "modal",
      section: props.gtmSection ? props.gtmSection : "saved videos",
      text: "owl dot",
    });
    setCurrentSlide(slide);
    setLoaded(false);
  };

  const handleClose = () => {
    generateGtmEvent({
      event: "modal",
      action: "close",
      name: "onclick",
      type: "click",
      region: "main content",
      section: `${videoTitle?.toLowerCase()}`,
      text: "close cross",
    });
    setCurrentSlide(0);
    props.onClose();
  };

  const handleNext = () => {
    generateGtmEvent({
      event: "select",
      action: "click",
      name: "onclick",
      type: "carousel",
      region: "main content",
      section: `${videoTitle?.toLowerCase()}`,
      text: "right chevron",
    });
    if (!props.skills) return;
    let nextSlide = currentSlide + 1;
    if (nextSlide === props.skills?.length) {
      nextSlide = 0;
    }
    setCurrentSlide(nextSlide);
  };

  const handlePrevious = () => {
    generateGtmEvent({
      event: "select",
      action: "click",
      name: "onclick",
      type: "carousel",
      region: "main content",
      section: `${videoTitle?.toLowerCase()}`,
      text: "left chevron",
    });
    if (!props.skills) return;
    let nextSlide = currentSlide - 1;
    if (nextSlide < 0) {
      nextSlide = props.skills.length - 1;
    }
    setCurrentSlide(nextSlide);
  };

  const handleSave = (skillId?: string) => {
    if (!skillId) return;
    generateGtmEvent({
      event: "select",
      action: currentVideoSaved ? "unclick" : "click",
      name: "onclick",
      type: "checkbox",
      region: "main content",
      section: `${videoTitle?.toLowerCase()}`,
      text: "save video button",
    });
    userCtx.toggleSavedSkill(skillId);
  };

  const handleViewAllVideos = () => {
    generateGtmEvent({
      event: "link",
      action: "click",
      name: "onclick",
      type: "internal link",
      region: "main content",
      section: `${videoTitle?.toLowerCase()}`,
      text: "view all videos",
    });
    history.push("/explore");
  };

  const runResizeScript = () => {
    let script = document.querySelector("#resize-script")!;
    if (!script) return;
    script.remove();
    script = document.createElement("script");
    script.setAttribute(
      "src",
      "https://asuorchard.asu.edu/modules/contrib/h5p/vendor/h5p/h5p-core/js/h5p-resizer.js"
    );
    script.setAttribute("charset", "UTF-8");
    script.setAttribute("id", "resize-script");

    let contentWrapper = document.querySelector("#iframe-content-wrapper")!;
    contentWrapper.appendChild(script);
  };

  let handleIframeLoad = () => {
    setLoaded(true);
    runResizeScript();
    let IframeWindow = iFrameRef.current?.contentWindow;
    let dataObject = {
      user: {
        email: currentUser.email, // required
        name: currentUser.full_name, // required
        user_id: currentUser.id, // required
      },
      client: "digit", // required client identifier (preferably a unique identifier e.g. macs)
      timestamp: new Date().toISOString().split(".")[0] + "Z", // required UTC timestamp (iframe render time)
      version: "1.0", // required
    };
    let domainObject = {
      domain: "",
    };
    if (iframeSrc.includes("staging")) {
      domainObject.domain = "https://staging.asuorchard.asu.edu";
      dataObject.user.email = dataObject.user.email.replace(/\+.*@/, "@"); // Added to remove the + from the email address because orchard is not accepting emails with + in them
    } else {
      domainObject.domain = "https://asuorchard.asu.edu";
    }
    postRobot
      .send(IframeWindow!, "provideUserInfo", dataObject, domainObject)
      .then(function (event: any) {
        //additional code after successfully sending the data
        console.log("data sent");
      })
      .catch(function (err: any) {
        // Handle any errors that stopped our call from going through
        console.log("error sending data");
        console.error(err.message);
      });
  };

  useEffect(() => {
    window.addEventListener("resize", runResizeScript);
  }, []);

  useEffect(() => {
    if (!currentVideo?.skill_id) return;
    setCurrentVideoSaved(userCtx.checkSkillSaved(currentVideo?.skill_id));
  }, [currentVideo, userCtx, userCtx.checkSkillSaved]);

  // commented out in case we decide to display the thumbnail while iFrame loads
  // const getCurrentVideoThumbnail = () => {
  //   if (
  //     !currentVideo ||
  //     !currentVideo.metadata ||
  //     !currentVideo.metadata.videoThumbnail
  //   )
  //     return "";
  //   return currentVideo.metadata.videoThumbnail;
  // };

  const vertBreakpointXs = useMediaQuery("(max-height:600px)");
  const vertBreakpointSm = useMediaQuery("(max-height:800px)");
  const vertBreakpointMd = useMediaQuery("(max-height:1200px)");

  const iframeContent = (
    <Box className={classes.videoContent} id="iframe-content-wrapper">
      {!loaded && (
        <Box className={classes.loaderBox}>
          <VideoBlank
            style={{
              width: "100%",
              height: "auto",
              paddingBottom: "36px",
            }}
          />
          <CircularProgress
            sx={{
              position: "absolute",
              alignSelf: "center",
              justifySelf: "center",
            }}
          />
        </Box>
      )}
      <iframe
        id={"remediation-iframe"}
        ref={iFrameRef}
        title={`Video: ${videoTitle}`}
        src={iframeSrc}
        frameBorder="0"
        allow="fullscreen"
        lang="en"
        className={classes.iframeStyle}
        onLoad={handleIframeLoad}
        height={400}
        style={
          !loaded ? { position: "absolute", top: "0px", left: "0px", opacity: 0 } : {}
        }
      />
      <script
        src="https://asuorchard.asu.edu/modules/contrib/h5p/vendor/h5p/h5p-core/js/h5p-resizer.js"
        charSet="UTF-8"
        id="resize-script"></script>
    </Box>
  );

  if (embedMode) {
    return iframeContent;
  } else {
    return (
      <Dialog
        open={props.open}
        onClose={handleClose}
        title=""
        maxWidth={
          vertBreakpointXs
            ? "xs"
            : vertBreakpointSm
            ? "sm"
            : vertBreakpointMd
            ? "md"
            : "lg"
        }
        fullWidth
        className={classes.root}>
        {showControls && (
          <>
            {currentSlide > 0 && (
              <Tooltip title="Previous video" placement="bottom" arrow>
                <Box className={`${classes.controlsButton} ${classes.previousButton}`}>
                  <IconButtonOutlined
                    className={classes.controlsButtonSize}
                    size="medium"
                    onClick={handlePrevious}>
                    <IconChevronLeft style={{ width: "26px", height: "26px" }} />
                  </IconButtonOutlined>
                </Box>
              </Tooltip>
            )}
            {props.skills && currentSlide < props.skills?.length - 1 && (
              <Tooltip title="Next video" placement="bottom" arrow>
                <Box className={`${classes.controlsButton} ${classes.nextButton}`}>
                  <IconButtonOutlined
                    className={classes.controlsButtonSize}
                    size="medium"
                    onClick={handleNext}>
                    <IconChevronRight style={{ width: "26px", height: "26px" }} />
                  </IconButtonOutlined>
                </Box>
              </Tooltip>
            )}
          </>
        )}

        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            ...(vertBreakpointXs && {
              paddingTop: "10px",
              paddingBottom: "10px",
            }),
          }}
          component="h2">
          <Typography component="span" variant="h6">
            {" "}
          </Typography>
          <Tooltip title="Close" arrow role="tooltip" describeChild>
            <IconButton
              onClick={handleClose}
              size="small"
              aria-label="Close Dialog"
              className={classes.closeButton}>
              <Close />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        {iframeContent}
        <DialogContent className={classes.dialogContent}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h4" className={classes.skillTitle}>
              {videoTitle}
            </Typography>

            <IconButtonOutlined
              size="small"
              className={classes.bookmarkButton}
              onClick={() => handleSave(currentVideo?.skill_id)}
              tooltipLabel={currentVideoSaved ? "Remove from saved videos" : "Save video"}
              tooltipPlacement="bottom">
              {currentVideoSaved ? (
                <IconBookmarkFill style={{ width: "18px", height: "18px" }} />
              ) : (
                <IconBookmark style={{ width: "18px", height: "18px" }} />
              )}
            </IconButtonOutlined>
          </Stack>
        </DialogContent>
        {showBottomNavigation && (
          <DialogActions
            sx={{
              padding: "0px 24px 18px 24px",
              marginTop: "0px",
              justifyContent: "space-between",
            }}>
            <Box>
              {props.skills?.map((skill, index) => {
                if (index === currentSlide) {
                  return (
                    <IconButtonContained
                      size="small"
                      className={`${classes.slideButton} ${classes.slideButtonActive}`}
                      key={`Video ${index + 1}`}
                    />
                  );
                } else {
                  return (
                    <IconButtonOutlined
                      size="small"
                      className={`${classes.slideButton} ${classes.slideButtonInactive}`}
                      onClick={() => handleChangeSlide(index)}
                      key={`Video ${index + 1}`}
                    />
                  );
                }
              })}
            </Box>
            <Link
              onClick={onClickViewAllVideos ? onClickViewAllVideos : handleViewAllVideos}>
              View all videos
            </Link>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}
